import { useEffect, useState, type ReactNode } from "react";
import type { ReportContentBlock, ReportSection } from "./domain";
import { getLocalFile } from "./services/local-file-store";
import { SectionOneClientBody, isInternalSectionOneBlock } from "./section1-client-output";
import { SectionTwoClientBody } from "./section2-client-output";
import { SectionThreeClientBody } from "./section3-client-output";
import { SectionFivePromptResultsView } from "./section5-prompt-results-view";
import { SectionSixClientBody, SimplifiedClientBody } from "./pages/SectionSixClientBody";
import { SectionTenClientBody } from "./section10-client-output";
import { resolveClientPresentation } from "./client-presentation";
import { ClientPresentationRenderer } from "./client-presentation-renderer";

const internalDisplayTitles = /^(summary|client point \d+)$/i;

type TextPart =
  | { type: "paragraph"; text: string }
  | { type: "unordered"; items: string[] }
  | { type: "ordered"; items: string[] };

export function clientTextParts(value: string): TextPart[] {
  const parts: TextPart[] = [];
  const paragraphs = value.replace(/\r\n/g, "\n").split(/\n{2,}/);
  for (const paragraph of paragraphs) {
    const lines = paragraph.split("\n").map(line => line.trim()).filter(Boolean);
    if (!lines.length) continue;
    const unordered = lines.every(line => /^(?:[-*•])\s+/.test(line));
    const ordered = lines.every(line => /^\d+[.)]\s+/.test(line));
    if (unordered) parts.push({ type: "unordered", items: lines.map(line => line.replace(/^(?:[-*•])\s+/, "")) });
    else if (ordered) parts.push({ type: "ordered", items: lines.map(line => line.replace(/^\d+[.)]\s+/, "")) });
    else parts.push({ type: "paragraph", text: lines.join("\n") });
  }
  return parts;
}

export function ClientReportText({ value }: { value: string }) {
  return <>{clientTextParts(value).map((part, index) => {
    if (part.type === "unordered") return <ul key={index}>{part.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>;
    if (part.type === "ordered") return <ol key={index}>{part.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ol>;
    return <p key={index}>{part.text}</p>;
  })}</>;
}

export function clientTable(value:string){
  const rows=value.replace(/\r\n/g,"\n").split("\n").map(line=>line.trim()).filter(Boolean).map(line=>line.split("|").map(cell=>cell.trim()));
  return rows.length>1&&rows.every(row=>row.length===rows[0].length)?rows:undefined;
}

function StoredReportImage({ block }: { block: ReportContentBlock }) {
  const [imageUrl, setImageUrl] = useState("");
  useEffect(() => {
    let url = "";
    let active = true;
    if (block.imageFileKey?.startsWith("data:image/")) setImageUrl(block.imageFileKey);
    else if (block.imageFileKey) getLocalFile(block.imageFileKey).then(file => {
      if (file && active) {
        url = URL.createObjectURL(file);
        setImageUrl(url);
      }
    });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [block.imageFileKey]);
  return imageUrl
    ? <img src={imageUrl} alt={block.alternativeText || block.title || "Report evidence screenshot"} />
    : <div className="image-placeholder">Locally stored screenshot</div>;
}

export function ClientReportBlock({ block }: { block: ReportContentBlock }) {
  if (block.type === "screenshot") {
    return <figure className={`report-image ${(block.imageLayout ?? "Full width").toLowerCase().replaceAll(" ", "-")}`}>
      <StoredReportImage block={block} />
      {block.content.trim() && <figcaption>{block.content}</figcaption>}
    </figure>;
  }
  if(block.type==="table"){
    const rows=clientTable(block.content);
    if(rows)return <section className="report-table">
      {block.title?.trim()&&<h2>{block.title}</h2>}
      <div className="report-table-scroll"><table><thead><tr>{rows[0].map((cell,index)=><th key={index} scope="col">{cell}</th>)}</tr></thead><tbody>{rows.slice(1).map((row,index)=><tr key={index}>{row.map((cell,cellIndex)=><td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>
    </section>;
  }
  const showTitle = Boolean(block.title?.trim()) && !internalDisplayTitles.test(block.title!.trim());
  return <div className={`report-block ${block.type.replaceAll(" ", "-")}`}>
    {showTitle && <h2>{block.title}</h2>}
    <ClientReportText value={block.content} />
  </div>;
}

function legacyCompetitors(section: ReportSection): ReactNode {
  if (section.id !== "competitors" || section.sectionSevenWorkflow?.simplifiedVersion) return null;
  return (section.competitors ?? []).filter(item => item.approved && item.classification === "Genuine competitor").map(item =>
    <article className="client-competitor" key={item.id}>
      <h2>{item.name}</h2>
      <p><strong>Why they matter:</strong> {item.clientSummary}</p>
      <p><strong>Where they appeared:</strong> {item.supportingSources}</p>
      <p><strong>What appears to support them:</strong> {item.authoritySignals}</p>
      <p><strong>What you can learn:</strong> {item.lesson}</p>
    </article>
  );
}

export function ClientReportSectionContent({
  section,
  organisationName,
  locked,
  reviewPresentation = false,
}: {
  section: ReportSection;
  organisationName: string;
  locked: boolean;
  reviewPresentation?: boolean;
}) {
  const presentation=resolveClientPresentation(section);
  if(presentation.document&&presentation.current&&(presentation.resolution==="Client section ready"||reviewPresentation)){
    return <div className="client-report-content"><ClientPresentationRenderer document={presentation.document}/></div>;
  }
  let body: ReactNode;
  if (section.id === "google-understanding") body = <SectionOneClientBody section={section} organisationName={organisationName} />;
  else if (section.id === "maps-profile") body = <SectionTwoClientBody section={section} />;
  else if (section.id === "website-review") body = <SectionThreeClientBody section={section} />;
  else if (section.id === "overall-findings" && section.sectionFiveWorkflow?.approvedPromptResults) body = <SectionFivePromptResultsView projection={section.sectionFiveWorkflow.approvedPromptResults} mode="client" preserveLegacyEntities={locked} />;
  else if (section.id === "commercial-gaps" && section.sectionSixWorkflow?.simplifiedVersion) body = <SectionSixClientBody section={section} />;
  else if (section.id === "competitors" && section.sectionSevenWorkflow?.simplifiedVersion) body = <SimplifiedClientBody section={section} blockPrefix="section7-client-v2-" />;
  else if (section.id === "authority-gaps" && section.sectionEightWorkflow?.simplifiedVersion) body = <SimplifiedClientBody section={section} blockPrefix="section8-client-v2-" />;
  else if (section.id === "positioning" && section.sectionNineWorkflow?.simplifiedVersion) body = <SimplifiedClientBody section={section} blockPrefix="section9-client-v2-" />;
  else if (section.id === "work-programme" && section.sectionTenWorkflow?.v2ClientDraftApproved) body = <SectionTenClientBody section={section} />;
  else {
    const blocks = (section.blocks ?? []).filter(block => block.clientVisible && !block.appendixOnly && block.content.trim() && !isInternalSectionOneBlock(block));
    body = blocks.length ? blocks.map(block => <ClientReportBlock block={block} key={block.id} />) : <p>No client-facing content has been added to this section yet.</p>;
  }
  return <div className="client-report-content">{body}{legacyCompetitors(section)}</div>;
}
